import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  useReactTable, getCoreRowModel, getSortedRowModel, getPaginationRowModel, flexRender, createColumnHelper,
} from '@tanstack/react-table';
import { motion, AnimatePresence, useMotionValue, useSpring, useMotionValueEvent } from 'motion/react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import { format as formatDateFns } from 'date-fns';
import { Menu } from '@base-ui-components/react/menu';
import {
  PieChart, Pie, Cell, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import {
  Download, AlertCircle, ChevronDown, ChevronLeft, ChevronRight,
  Calendar as CalendarIcon, ArrowUpDown, ArrowUp, ArrowDown, ArrowLeft, X,
} from 'lucide-react';
import { API_BASE } from '../config';
import { formatDate } from '../utils/formatDate';
import { notifyEditBlocked } from '../components/EditAccessToast';
import DataFreshness from '../components/DataFreshness';
import { useSwrStore, useSwrQuery } from '../swrCache';

/* ============================================================================
 * Shopify Tracker — order fulfillment stats for D2C.
 *
 * Deliberately built as a sibling of GRNManagement.jsx rather than a new visual
 * language: same CSS-variable theming (renamed to --shopify-*), same beam
 * header, same persistent 2/3 (charts + table) / 1/3 (KPI <-> order detail)
 * split where the right column swaps content IN PLACE and never opens as an
 * overlay, same hand-rolled DonutChart / Counter / FluidStatusTabs /
 * DateRangePicker / Pagination, same TanStack-table-as-styled-divs pattern.
 *
 * Two server calls, both through useSwrQuery (../swrCache.js) so flipping the
 * granularity/date range back and forth doesn't re-ask the database the same
 * question:
 *   - get-website-order-stats            -> bucketed fulfilled/unfulfilled
 *   - get-website-unfulfilled-orders     -> the drill-down table
 *
 * Both endpoints already exclude cancelled orders server-side, so nothing here
 * needs to filter on cancelled_at.
 * ========================================================================== */

const ICON = { xs: 12, sm: 14, md: 16, lg: 20 };
const SHADOW_SM = 'var(--shopify-shadow-sm)';
const SHADOW_MD = 'var(--shopify-shadow-md)';
const SHADOW_LG = 'var(--shopify-shadow-lg)';
const STICKY_TOP = 86; // approx header-bar height + gap; the table header and right column both stick below it

const GRID_COLS = '1fr 0.95fr 1.05fr 1fr 0.85fr 1.05fr';
const TABLE_MIN_WIDTH = 760;

const FULFILLED_COLOR = 'var(--shopify-green)';
const UNFULFILLED_COLOR = 'var(--shopify-amber)';

const GRANULARITY_TABS = [
  { key: 'day', label: 'Daily', emoji: '📅' },
  { key: 'week', label: 'Weekly', emoji: '🗓️' },
  { key: 'month', label: 'Monthly', emoji: '📆' },
];

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_OPTIONS = MONTH_LABELS.map((m, i) => ({ label: m, value: String(i + 1).padStart(2, '0') }));
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR - 2, CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1].map(y => ({ label: String(y), value: String(y) }));

/* ---------------------------------------------------------------------- */
/* Dates / formatting                                                      */
/* ---------------------------------------------------------------------- */

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
const DEFAULT_FROM = () => isoDaysAgo(30);
const DEFAULT_TO = () => todayISO();

// UTC calendar fields, not local ones: SQL hands back the bucket start as a
// midnight-UTC timestamp, and reading it with local getters rolls the day back
// in any timezone behind UTC (and forward past it in none, but the asymmetry is
// exactly the class of off-by-one-day bug this page can least afford).
function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${formatDate(value)}, ${hh}:${mm}`;
}
// The x-axis label for one stats bucket, per the granularity that produced it.
function formatBucket(value, granularity) {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  const day = d.getUTCDate();
  const mon = MONTH_LABELS[d.getUTCMonth()];
  if (granularity === 'month') return `${mon} ${d.getUTCFullYear()}`;
  if (granularity === 'week') return `Wk of ${day} ${mon}`;
  return `${day} ${mon}`;
}
// A real Excel date cell (not formatted text) so the sheet's own sort/filter/
// date-math work. Built from Date.UTC so SheetJS's date->serial conversion —
// which reads the JS Date's raw epoch — lands on a whole serial rather than a
// fractional one offset by the runtime's UTC offset.
function toExcelDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

function fulfillmentStyle(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'fulfilled') return { bg: 'var(--shopify-green-tint)', color: 'var(--shopify-green)', border: 'var(--shopify-green-border)' };
  if (s === 'partial' || s === 'partially_fulfilled') return { bg: 'var(--shopify-blue-tint)', color: 'var(--shopify-blue)', border: 'var(--shopify-blue-tint)' };
  return { bg: 'var(--shopify-amber-tint)', color: 'var(--shopify-amber)', border: 'var(--shopify-amber-border)' };
}
function prettyStatus(status) {
  if (status === null || status === undefined || status === '') return 'unfulfilled';
  return String(status).replace(/_/g, ' ');
}
function formatMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (isNaN(n)) return String(value);
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/* ---------------------------------------------------------------------- */
/* Counter — spring count-up on mount/value change, same as GRN's.         */
/* ---------------------------------------------------------------------- */
function Counter({ value, style }) {
  const ref = useRef(null);
  const motionValue = useMotionValue(0);
  const springValue = useSpring(motionValue, { damping: 60, stiffness: 80 });

  useEffect(() => { motionValue.set(value || 0); }, [value, motionValue]);
  useMotionValueEvent(springValue, 'change', (v) => {
    if (ref.current) ref.current.textContent = Math.round(v).toLocaleString('en-IN');
  });

  return <span ref={ref} style={{ ...S.numTab, ...style }}>0</span>;
}

/* ---------------------------------------------------------------------- */
/* Donut chart — hand-rolled single-value SVG ring (stroke-dasharray /     */
/* dashoffset) that animates in on mount via a delayed state flip. Used    */
/* here for the headline Fill Rate %.                                     */
/* ---------------------------------------------------------------------- */
function DonutChart({ size = 128, progress, strokeWidth = 13, trackColor = '#F1EEF6', progressColor = '#5C7E60', children }) {
  const [animate, setAnimate] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setAnimate(true), 250);
    return () => clearTimeout(t);
  }, []);
  const radius = size / 2 - strokeWidth / 2;
  const circumference = Math.PI * radius * 2;
  const offset = animate ? circumference * ((100 - progress) / 100) : circumference;

  return (
    <div style={{ position: 'relative', width: size, height: size, flex: 'none' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
        <circle r={radius} cx={size / 2} cy={size / 2} fill="transparent" stroke={trackColor} strokeWidth={strokeWidth} />
        <circle
          r={radius} cx={size / 2} cy={size / 2} fill="transparent" stroke={progressColor} strokeWidth={strokeWidth}
          strokeLinecap="round" strokeDasharray={`${circumference}px`} strokeDashoffset={`${offset}px`}
          style={{ transition: 'stroke-dashoffset 700ms cubic-bezier(0.34, 1.28, 0.64, 1)' }}
        />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        {children}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Granularity tabs — shared layoutId pill that physically slides between  */
/* tabs (spring: stiffness 380, damping 34), not a re-paint.               */
/* ---------------------------------------------------------------------- */
function FluidStatusTabs({ value, onChange }) {
  return (
    <nav aria-label="Granularity" style={S.fluidNav}>
      <div role="tablist" style={S.fluidList}>
        {GRANULARITY_TABS.map((opt) => {
          const active = opt.key === value;
          return (
            <button
              key={opt.key} type="button" role="tab" aria-selected={active} className="shopify-tab"
              onClick={() => onChange(opt.key)} style={S.fluidTab}
            >
              {active && (
                <motion.span
                  layoutId="shopify-granularity-pill" style={S.fluidPill}
                  transition={{ type: 'spring', stiffness: 380, damping: 34, mass: 0.75 }}
                />
              )}
              <span style={{ ...S.fluidTabContent, color: active ? '#fff' : 'var(--shopify-text-muted)' }}>
                <span aria-hidden style={{ fontSize: 13, filter: 'grayscale(1)' }}>{opt.emoji}</span>
                {opt.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

/* react-day-picker's own dropdown caption (captionLayout="dropdown") renders a
   native <select> by default. Overridden via the `components` prop with the
   same Base UI Menu popup used everywhere else on the page. react-day-picker
   calls onChange with a native-event-shaped object ({ target: { value } }), so
   the Base UI radio selection is adapted back into that shape. */
function CalendarDropdown({ options, value, onChange, disabled }) {
  const current = options?.find(o => String(o.value) === String(value));
  return (
    <Menu.Root>
      <Menu.Trigger disabled={disabled} style={S.calendarCaptionTrigger}>
        {current?.label}
        <ChevronDown size={12} />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner sideOffset={4} align="start" style={{ zIndex: 500 }}>
          <Menu.Popup style={S.menuPopup}>
            <Menu.RadioGroup value={String(value)} onValueChange={(v) => onChange({ target: { value: v } })}>
              {options?.map(o => (
                <Menu.RadioItem key={o.value} value={String(o.value)} disabled={o.disabled} style={S.menuItem}>{o.label}</Menu.RadioItem>
              ))}
            </Menu.RadioGroup>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}

/* ---------------------------------------------------------------------- */
/* Date range picker — react-day-picker with dropdown month/year captions,  */
/* restyled to the portal palette via .shopify-calendar CSS below.          */
/* ---------------------------------------------------------------------- */
function DateRangePicker({ from, to, onChange, onClear }) {
  const [open, setOpen] = useState(false);
  const range = { from: from ? new Date(`${from}T00:00:00`) : undefined, to: to ? new Date(`${to}T00:00:00`) : undefined };
  const label = from || to
    ? `${from ? formatDateFns(new Date(`${from}T00:00:00`), 'd MMM yyyy') : '…'} – ${to ? formatDateFns(new Date(`${to}T00:00:00`), 'd MMM yyyy') : '…'}`
    : 'Date range';

  return (
    <div style={{ position: 'relative' }}>
      <button type="button" className="shopify-tab" style={S.dateBtn} onClick={() => setOpen(o => !o)}>
        <CalendarIcon size={ICON.sm} />{label}
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 499 }} />
          <div className="shopify-calendar" style={S.calendarPopover}>
            <DayPicker
              mode="range"
              captionLayout="dropdown"
              components={{ Dropdown: CalendarDropdown }}
              selected={range}
              defaultMonth={range.from}
              numberOfMonths={2}
              onSelect={(r) => {
                // NOT .toISOString().slice(0,10) — that converts the clicked
                // day's LOCAL midnight to UTC, which rolls back to the previous
                // day in any timezone ahead of UTC (e.g. IST). date-fns format()
                // reads local y/m/d components directly, no UTC conversion.
                onChange(r?.from ? formatDateFns(r.from, 'yyyy-MM-dd') : '', r?.to ? formatDateFns(r.to, 'yyyy-MM-dd') : '');
              }}
            />
            {(from || to) && (
              <button type="button" style={S.clearCalBtn} onClick={() => { onClear(); setOpen(false); }}>Reset dates</button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Pagination — numbered pages with smart ellipsis, driven entirely by the  */
/* TanStack Table instance so the copies above and below the table can       */
/* never drift out of sync with each other or the table.                    */
/* ---------------------------------------------------------------------- */
function pageNumbersWithEllipsis(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  if (current > 3) pages.push('ellipsis-start');
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (current < total - 2) pages.push('ellipsis-end');
  pages.push(total);
  return pages;
}
function Pagination({ table }) {
  const pageCount = Math.max(1, table.getPageCount());
  const current = table.getState().pagination.pageIndex + 1;
  if (pageCount <= 1) return null;
  const pages = pageNumbersWithEllipsis(current, pageCount);
  return (
    <nav aria-label="Pagination" style={S.paginationNav}>
      <button type="button" className="shopify-tab" style={{ ...S.paginationArrow, opacity: table.getCanPreviousPage() ? 1 : 0.4 }}
        onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
        <ChevronLeft size={ICON.sm} />
      </button>
      {pages.map((p, i) => (
        typeof p === 'number' ? (
          <button key={p} type="button" className="shopify-tab"
            style={{ ...S.paginationLink, ...(p === current ? S.paginationLinkActive : null) }}
            onClick={() => table.setPageIndex(p - 1)}
          >
            {p}
          </button>
        ) : (
          <span key={p + i} style={S.paginationEllipsis}>…</span>
        )
      ))}
      <button type="button" className="shopify-tab" style={{ ...S.paginationArrow, opacity: table.getCanNextPage() ? 1 : 0.4 }}
        onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
        <ChevronRight size={ICON.sm} />
      </button>
    </nav>
  );
}

/* ---------------------------------------------------------------------- */
/* Header background — fixed-width diagonal lanes, ~30% carrying a          */
/* randomized "meteor" streak on a loop (deterministic seeded-random, so    */
/* the pattern is stable across renders instead of reshuffling).            */
/* ---------------------------------------------------------------------- */
const prand = (n) => {
  const x = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
};
const lerp = (min, max, t) => min + (max - min) * t;
const HEADER_LANES = 40;

function HeaderBeam({ index }) {
  const fast = prand(index + 91) < 0.3;
  const duration = fast ? lerp(1.2, 3.2, prand(index)) : lerp(6, 14, prand(index));
  const delay = lerp(0, 9, prand(index + 13));
  const length = Math.round(lerp(10, 26, prand(index + 29)));
  const width = Math.max(2, Math.round(lerp(2, 4, prand(index + 53))));
  const opacity = fast ? lerp(0.32, 0.5, prand(index + 71)) : lerp(0.14, 0.32, prand(index + 71));
  return (
    <div
      className="shopify-beam"
      style={{
        '--duration': `${duration.toFixed(2)}s`, '--delay': `${delay.toFixed(2)}s`,
        '--length': `${length}px`, '--opacity': opacity.toFixed(2), width: `${width}px`,
      }}
    />
  );
}
function HeaderBeamBackground() {
  return (
    <div className="shopify-beam-bg" aria-hidden="true">
      {Array.from({ length: HEADER_LANES }, (_, i) => (
        <div key={i} className="shopify-beam-lane">
          {prand(i + 7) < 0.3 && <HeaderBeam index={i + 1} />}
        </div>
      ))}
    </div>
  );
}

/* Dark-mode-only page background — large, heavily-blurred circles running a
   scale-pulse keyframe, mix-blend-mode: screen so they glow against the dark
   backdrop instead of vanishing. Mounted only when dark (a discrete theme
   choice, not a media feature — CSS alone can't gate this). */
const BLOB_COLORS = ['#6E58A6', '#A48FD1', '#E08BA0'];
function ShopifyBlurryBlobBackground() {
  return (
    <div className="shopify-blob-bg" aria-hidden="true">
      {BLOB_COLORS.map((color, i) => (
        <div key={color} className="shopify-blob" style={{ background: color, animationDelay: `${i * 1.6}s` }} />
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Month/Year quick filters — Base UI Menu, single-select via RadioGroup.   */
/* ---------------------------------------------------------------------- */
function MonthYearMenu({ label, value, options, onChange }) {
  const current = options.find(o => o.value === value);
  return (
    <Menu.Root>
      <Menu.Trigger className="shopify-tab" style={S.dateBtn}>
        {current ? current.label : label}
        <ChevronDown size={ICON.xs} />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner sideOffset={6} align="start" style={{ zIndex: 500 }}>
          <Menu.Popup style={S.menuPopup}>
            <Menu.RadioGroup value={value} onValueChange={onChange}>
              <Menu.RadioItem value="" style={S.menuItem}>Any {label.toLowerCase()}</Menu.RadioItem>
              {options.map(o => (
                <Menu.RadioItem key={o.value} value={o.value} style={S.menuItem}>{o.label}</Menu.RadioItem>
              ))}
            </Menu.RadioGroup>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}

/* ---------------------------------------------------------------------- */
/* Charts                                                                  */
/* ---------------------------------------------------------------------- */
function ChartCard({ title, subtitle, children, height = 260 }) {
  return (
    <div style={S.chartCard}>
      <div style={{ marginBottom: 10 }}>
        <div style={S.chartTitle}>{title}</div>
        {subtitle && <div style={S.chartSubtitle}>{subtitle}</div>}
      </div>
      <div style={{ width: '100%', height }}>{children}</div>
    </div>
  );
}

const TOOLTIP_STYLE = {
  background: 'var(--shopify-surface)',
  border: '1px solid var(--shopify-border)',
  borderRadius: 10,
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: 12.5,
  color: 'var(--shopify-text)',
  boxShadow: SHADOW_MD,
};

function FulfillmentPie({ fulfilled, unfulfilled }) {
  const total = fulfilled + unfulfilled;
  const data = [
    { name: 'Fulfilled', value: fulfilled, color: FULFILLED_COLOR },
    { name: 'Unfulfilled', value: unfulfilled, color: UNFULFILLED_COLOR },
  ];
  if (total === 0) return <div style={S.chartEmpty}>No orders in this range.</div>;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          innerRadius="45%"
          outerRadius="75%"
          paddingAngle={2}
          stroke="var(--shopify-surface)"
          strokeWidth={2}
          isAnimationActive
          label={({ name, value }) => `${name} ${Math.round((value / total) * 100)}%`}
          labelLine={false}
        >
          {data.map(d => <Cell key={d.name} fill={d.color} />)}
        </Pie>
        <Tooltip
          contentStyle={TOOLTIP_STYLE}
          formatter={(value, name) => [`${Number(value).toLocaleString('en-IN')} (${Math.round((value / total) * 100)}%)`, name]}
        />
        <Legend wrapperStyle={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 12.5 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

function FulfillmentBars({ data }) {
  if (data.length === 0) return <div style={S.chartEmpty}>No orders in this range.</div>;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 4, right: 8, left: -14, bottom: 4 }} barGap={2}>
        <CartesianGrid stroke="var(--shopify-border)" vertical={false} />
        <XAxis
          dataKey="label"
          tick={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, fill: 'var(--shopify-text-muted)' }}
          stroke="var(--shopify-border-soft)"
          interval="preserveStartEnd"
        />
        <YAxis
          allowDecimals={false}
          tick={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, fill: 'var(--shopify-text-muted)' }}
          stroke="var(--shopify-border-soft)"
        />
        <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'var(--shopify-purple-tint)' }} />
        <Legend wrapperStyle={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 12.5 }} />
        <Bar dataKey="Fulfilled" fill={FULFILLED_COLOR} radius={[4, 4, 0, 0]} />
        <Bar dataKey="Unfulfilled" fill={UNFULFILLED_COLOR} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/* ---------------------------------------------------------------------- */
/* Right column — KPI view (default) and order detail view (swapped in when */
/* a row is selected). Same column, same width, content crossfades —        */
/* never an overlay/drawer.                                                 */
/* ---------------------------------------------------------------------- */
function KpiView({ fulfilled, unfulfilled }) {
  const total = fulfilled + unfulfilled;
  const pct = total ? Math.round((fulfilled / total) * 100) : 0;
  return (
    <motion.div key="kpi" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }}>
      <div style={S.bentoGrid}>
        {/* Row 1 — hero cell: the fill-rate ring, full width */}
        <div style={{ ...S.bentoCard, ...S.bentoSpan2, alignItems: 'center', textAlign: 'center', background: 'var(--shopify-surface-subtle)' }}>
          <DonutChart size={112} progress={pct} progressColor="var(--shopify-green)" trackColor="var(--shopify-border)">
            <span style={{ ...S.numTab, fontSize: 22, fontWeight: 700, color: 'var(--shopify-text)' }}>{pct}%</span>
          </DonutChart>
          <div style={{ fontSize: 11.5, color: 'var(--shopify-text-muted)', fontWeight: 600, marginTop: 8 }}>Fill Rate (fulfilled ÷ total)</div>
        </div>

        {/* Row 2 — headline stat banner, full width, deliberately dark in both themes */}
        <div style={{ ...S.bentoCard, ...S.bentoSpan2, background: 'var(--shopify-accent-surface)' }}>
          <div style={{ fontSize: 10.5, color: '#B9AEDD', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total Orders</div>
          <Counter value={total} style={{ fontSize: 30, fontWeight: 700, color: '#fff' }} />
        </div>

        {/* Row 3 — two half-width cards */}
        <div style={{ ...S.bentoCard, background: 'var(--shopify-green-tint)' }}>
          <div style={{ ...S.statLabel, color: 'var(--shopify-green)' }}>Fulfilled</div>
          <Counter value={fulfilled} style={{ fontSize: 22, fontWeight: 700, color: 'var(--shopify-green)' }} />
        </div>
        <div style={{ ...S.bentoCard, background: 'var(--shopify-amber-tint)' }}>
          <div style={{ ...S.statLabel, color: 'var(--shopify-amber)' }}>Unfulfilled</div>
          <Counter value={unfulfilled} style={{ fontSize: 22, fontWeight: 700, color: 'var(--shopify-amber)' }} />
        </div>
      </div>
    </motion.div>
  );
}

function DetailView({ order, onBack }) {
  const st = fulfillmentStyle(order.fulfillment_status);
  return (
    <motion.div key="detail" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
      <button type="button" onClick={onBack} style={S.backBtn}><ArrowLeft size={ICON.sm} />Back to overview</button>

      <div style={S.panelHeader}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: "'Space Grotesk'", fontSize: 18, fontWeight: 600, color: 'var(--shopify-text)' }}>
            {order.order_number ? `#${order.order_number}` : '—'}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--shopify-text-muted)', marginTop: 2 }}>
            Placed {formatDate(order.created_at)}
          </div>
        </div>
      </div>

      <div style={{ ...S.bentoGrid, padding: '0 20px 20px' }}>
        <div style={{ ...S.bentoCard, ...S.bentoSpan2, background: 'var(--shopify-surface-subtle)', alignItems: 'flex-start' }}>
          <div style={{ ...S.statLabel, color: 'var(--shopify-text-muted)' }}>Fulfillment Status</div>
          <span style={{ ...S.statusBadge, background: st.bg, color: st.color, borderColor: st.border, width: 'fit-content' }}>
            {prettyStatus(order.fulfillment_status)}
          </span>
        </div>

        <div style={{ ...S.bentoCard, background: 'var(--shopify-blue-tint)', alignItems: 'flex-start' }}>
          <div style={{ ...S.statLabel, color: 'var(--shopify-blue)' }}>Financial</div>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--shopify-blue)' }}>{prettyStatus(order.financial_status)}</div>
        </div>
        <div style={{ ...S.bentoCard, background: 'var(--shopify-green-tint)', alignItems: 'flex-start' }}>
          <div style={{ ...S.statLabel, color: 'var(--shopify-green)' }}>Total Price</div>
          <div style={{ ...S.numTab, fontSize: 15, fontWeight: 700, color: 'var(--shopify-green)' }}>{formatMoney(order.total_price)}</div>
        </div>

        <div style={{ ...S.bentoCard, ...S.bentoSpan2, background: 'var(--shopify-surface-subtle)', alignItems: 'flex-start' }}>
          <div style={{ ...S.statLabel, color: 'var(--shopify-text-muted)' }}>Order Date</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--shopify-text)' }}>{formatDateTime(order.created_at)}</div>
        </div>

        <div style={{ ...S.bentoCard, ...S.bentoSpan2, background: 'var(--shopify-purple-tint)', alignItems: 'flex-start' }}>
          <div style={{ ...S.statLabel, color: 'var(--shopify-purple)' }}>Last Updated</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--shopify-purple)' }}>{formatDateTime(order.updated_at)}</div>
        </div>

        <div style={{ ...S.bentoCard, ...S.bentoSpan2, background: 'var(--shopify-surface-subtle)', alignItems: 'flex-start' }}>
          <div style={{ ...S.statLabel, color: 'var(--shopify-text-muted)' }}>Order ID</div>
          <div style={{ ...S.numTab, fontSize: 12.5, fontWeight: 600, color: 'var(--shopify-text-faint)', wordBreak: 'break-all' }}>{order.order_id}</div>
        </div>
      </div>
    </motion.div>
  );
}

/* ---------------------------------------------------------------------- */
/* Component                                                               */
/* ---------------------------------------------------------------------- */
// eslint-disable-next-line no-unused-vars
export default function ShopifyTracker({ user, theme }) {
  const dark = theme === 'dark';
  // Mirrored onto <html> (not just this page's own root div) because Base UI's
  // Menu.Portal renders dropdown popups onto document.body — a :root attribute
  // is visible to that portaled content through normal CSS variable
  // inheritance; an attribute on our own div wouldn't be, since a portal breaks
  // DOM (not React-tree) ancestry. Cleared on unmount so leaving this page
  // can't leave the whole document flagged dark.
  useEffect(() => {
    const root = document.documentElement;
    if (dark) root.setAttribute('data-shopify-theme', 'dark'); else root.removeAttribute('data-shopify-theme');
    return () => root.removeAttribute('data-shopify-theme');
  }, [dark]);

  const [granularity, setGranularity] = useState('day');
  const [dateFrom, setDateFrom] = useState(DEFAULT_FROM);
  const [dateTo, setDateTo] = useState(DEFAULT_TO);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  // Two stores rather than one: a stats entry is a handful of bucket rows, an
  // unfulfilled-orders entry is the full order list for a range. Sharing one
  // LRU would let a few big order lists evict every cheap stats entry.
  const statsStore = useSwrStore(20);
  const ordersStore = useSwrStore(8);

  // The key IS the query string, so a key that forgot a filter physically
  // cannot serve the wrong rows (see swrCache.js).
  const statsKey = useMemo(() => {
    const qs = new URLSearchParams();
    if (dateFrom) qs.set('from', dateFrom);
    if (dateTo) qs.set('to', dateTo);
    qs.set('granularity', granularity);
    return qs.toString();
  }, [dateFrom, dateTo, granularity]);

  const ordersKey = useMemo(() => {
    const qs = new URLSearchParams();
    if (dateFrom) qs.set('from', dateFrom);
    if (dateTo) qs.set('to', dateTo);
    return qs.toString();
  }, [dateFrom, dateTo]);

  const statsQuery = useSwrQuery(statsStore, statsKey, useCallback(async () => {
    const res = await fetch(`${API_BASE}/get-website-order-stats${statsKey ? '?' + statsKey : ''}`);
    const data = await res.json();
    if (!res.ok) throw new Error(data?.error || data?.message || `Server returned ${res.status}`);
    return { rows: Array.isArray(data.rows) ? data.rows : [], last_synced_at: data.last_synced_at ?? null };
  }, [statsKey]));

  const ordersQuery = useSwrQuery(ordersStore, ordersKey, useCallback(async () => {
    const res = await fetch(`${API_BASE}/get-website-unfulfilled-orders${ordersKey ? '?' + ordersKey : ''}`);
    const data = await res.json();
    if (!res.ok) throw new Error(data?.error || data?.message || `Server returned ${res.status}`);
    return Array.isArray(data) ? data : [];
  }, [ordersKey]));

  const statsRows = useMemo(() => statsQuery.data?.rows || [], [statsQuery.data]);
  const lastSyncedAt = statsQuery.data?.last_synced_at || null;
  // Memoised so the identity is stable while the data is unchanged — otherwise
  // the `|| []` allocates a new array every render, which would hand TanStack
  // Table a brand-new `data` reference and force a full re-sort/re-paginate on
  // every render instead of only when the data actually changed.
  const orderRows = useMemo(() => ordersQuery.data || [], [ordersQuery.data]);

  const loadError = statsQuery.error || ordersQuery.error;
  // One toast per distinct error message, not per render — the effect only
  // re-runs when the message itself changes.
  useEffect(() => {
    if (loadError) notifyEditBlocked(`Failed to load Shopify data: ${loadError}`);
  }, [loadError]);

  const totals = useMemo(() => statsRows.reduce((acc, r) => {
    acc.fulfilled += Number(r.fulfilled) || 0;
    acc.unfulfilled += Number(r.unfulfilled) || 0;
    return acc;
  }, { fulfilled: 0, unfulfilled: 0 }), [statsRows]);

  const chartData = useMemo(() => statsRows.map(r => ({
    label: formatBucket(r.date, granularity),
    Fulfilled: Number(r.fulfilled) || 0,
    Unfulfilled: Number(r.unfulfilled) || 0,
  })), [statsRows, granularity]);

  const selectedOrder = selectedOrderId
    ? orderRows.find(r => String(r.order_id) === String(selectedOrderId)) || null
    : null;

  function applyMonthYear(month, year) {
    if (!month && !year) return;
    const y = year || CURRENT_YEAR;
    // formatDateFns, not .toISOString() — same local-midnight-to-UTC rollback
    // bug as the calendar's onSelect above.
    if (month) {
      setDateFrom(formatDateFns(new Date(y, month - 1, 1), 'yyyy-MM-dd'));
      setDateTo(formatDateFns(new Date(y, month, 0), 'yyyy-MM-dd'));
    } else {
      setDateFrom(formatDateFns(new Date(y, 0, 1), 'yyyy-MM-dd'));
      setDateTo(formatDateFns(new Date(y, 11, 31), 'yyyy-MM-dd'));
    }
  }
  function clearFilters() {
    setGranularity('day');
    setDateFrom(DEFAULT_FROM());
    setDateTo(DEFAULT_TO());
    setSelectedMonth('');
    setSelectedYear('');
    setSelectedOrderId(null);
  }
  const isDefaultRange = granularity === 'day' && dateFrom === DEFAULT_FROM() && dateTo === DEFAULT_TO() && !selectedMonth && !selectedYear;

  function refreshAll() {
    statsQuery.refresh();
    ordersQuery.refresh();
  }

  function exportExcel() {
    const XLSX = window.XLSX;
    if (!XLSX) { alert('SheetJS not loaded'); return; }
    const headers = ['SR.', 'Order Number', 'Order Date', 'Fulfillment Status', 'Financial Status', 'Total Price', 'Updated At'];
    const dataRows = orderRows.map((r, i) => [
      i + 1,
      r.order_number ?? '',
      toExcelDate(r.created_at),
      prettyStatus(r.fulfillment_status),
      prettyStatus(r.financial_status),
      r.total_price != null && r.total_price !== '' ? Number(r.total_price) : '',
      toExcelDate(r.updated_at),
    ]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
    // aoa_to_sheet stores a JS Date input as a type 'n' cell carrying a default
    // numFmt (m/d/yy), not a type 'd' cell — override that numFmt so every date
    // column displays (and sorts) the same, explicit way.
    const DATE_COLS = [2, 6];
    for (let r = 1; r <= dataRows.length; r++) {
      DATE_COLS.forEach(c => {
        const cell = ws[XLSX.utils.encode_cell({ r, c })];
        if (cell && cell.t === 'n') cell.z = 'dd-mmm-yyyy';
      });
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Unfulfilled Orders');
    XLSX.writeFile(wb, `Shopify_Unfulfilled_Orders_${todayISO()}.xlsx`);
    notifyEditBlocked('Export ready');
  }

  /* ---- TanStack table: headless sorting over every column ---- */
  const columnHelper = useMemo(() => createColumnHelper(), []);
  const columns = useMemo(() => [
    columnHelper.accessor('order_number', {
      header: 'Order Number',
      cell: i => (
        <span style={{ color: 'var(--shopify-purple)', fontWeight: 600 }}>
          {i.getValue() ? `#${i.getValue()}` : '—'}
        </span>
      ),
    }),
    columnHelper.accessor('created_at', {
      header: 'Order Date', cell: i => formatDate(i.getValue()), sortingFn: 'datetime',
    }),
    columnHelper.accessor('fulfillment_status', {
      header: 'Fulfillment',
      cell: i => {
        const st = fulfillmentStyle(i.getValue());
        return (
          <span style={{ ...S.statusBadge, background: st.bg, color: st.color, borderColor: st.border, width: 'fit-content' }}>
            {prettyStatus(i.getValue())}
          </span>
        );
      },
    }),
    columnHelper.accessor('financial_status', {
      header: 'Financial', cell: i => <span style={{ color: 'var(--shopify-text-muted)', fontWeight: 500 }}>{prettyStatus(i.getValue())}</span>,
    }),
    columnHelper.accessor(r => (r.total_price == null || r.total_price === '' ? null : Number(r.total_price)), {
      id: 'total_price', header: 'Total Price',
      cell: i => <span style={{ ...S.numTab, fontWeight: 700, color: 'var(--shopify-text)' }}>{formatMoney(i.getValue())}</span>,
    }),
    columnHelper.accessor('updated_at', {
      header: 'Updated At', cell: i => formatDateTime(i.getValue()), sortingFn: 'datetime',
    }),
  ], [columnHelper]);

  const table = useReactTable({
    data: orderRows,
    columns,
    state: { sorting, pagination },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  // Whenever the row set changes, the current page may no longer exist (e.g.
  // page 3 of a now-shorter list) — snap back to page 1 rather than showing an
  // empty page.
  useEffect(() => { setPagination(p => (p.pageIndex === 0 ? p : { ...p, pageIndex: 0 })); }, [orderRows.length]);

  const loading = statsQuery.loading || ordersQuery.loading;

  return (
    <div className="shopify-page" data-tutorial="shopify-filters" data-shopify-theme={dark ? 'dark' : undefined} style={S.page}>
      <style>{EMBEDDED_CSS}</style>
      {dark && <ShopifyBlurryBlobBackground />}

      {/* Unified sticky header bar. The beam background sits at z-index:0;
          title/actions get z-index:1 so they stay fully legible over it. */}
      <div style={S.headerBar}>
        <HeaderBeamBackground />
        <h1 style={{ ...S.h1, position: 'relative', zIndex: 1 }}>Shopify <span style={{ fontWeight: 600, color: '#C9B7EA' }}>Tracker</span></h1>
        <div style={{ ...S.headerActions, position: 'relative', zIndex: 1 }}>
          <button style={S.btnGreen} onClick={exportExcel}><Download size={ICON.sm} />Download Excel</button>
          {/* Two different timestamps, deliberately shown separately:
              DataFreshness is when THIS BROWSER last called the API (the
              cache-age question), while last_synced_at is when the Shopify
              sync job last pulled orders into our database — the number that
              actually bounds how current these counts can possibly be. */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
            <DataFreshness fetchedAt={statsQuery.fetchedAt} loading={loading} onRefresh={refreshAll} />
            <span style={S.syncNote}>
              Shopify data as of {lastSyncedAt ? formatDateTime(lastSyncedAt) : 'never synced'}
            </span>
          </div>
        </div>
      </div>

      {/* Persistent 2/3 (charts + table) / 1/3 (KPI <-> order detail) split.
          Below ~1024px, .shopify-body/.shopify-right-col CSS (media query,
          !important since inline styles otherwise always win) drop this to a
          single stacked column instead of squeezing the split at any width. */}
      <div className="shopify-body" style={S.body}>
        <div style={S.leftCol}>
          <div style={S.filterBar}>
            <FluidStatusTabs value={granularity} onChange={(v) => { setGranularity(v); setSelectedOrderId(null); }} />
            <div style={S.filterDivider} />
            <DateRangePicker
              from={dateFrom} to={dateTo}
              onChange={(f, t) => { setDateFrom(f); setDateTo(t); setSelectedOrderId(null); }}
              onClear={() => { setDateFrom(DEFAULT_FROM()); setDateTo(DEFAULT_TO()); setSelectedMonth(''); setSelectedYear(''); }}
            />
            <MonthYearMenu label="Month" value={selectedMonth} options={MONTH_OPTIONS}
              onChange={(m) => { setSelectedMonth(m); applyMonthYear(m ? Number(m) : null, selectedYear ? Number(selectedYear) : null); }} />
            <MonthYearMenu label="Year" value={selectedYear} options={YEAR_OPTIONS}
              onChange={(y) => { setSelectedYear(y); applyMonthYear(selectedMonth ? Number(selectedMonth) : null, y ? Number(y) : null); }} />
            {!isDefaultRange && (
              <button type="button" className="shopify-tab" style={S.clearFiltersBtn} onClick={clearFilters}>
                <X size={ICON.xs} />Clear filters
              </button>
            )}
          </div>

          {loadError && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderRadius: 10, background: 'var(--shopify-pink-tint)', color: 'var(--shopify-error)', fontSize: 13, marginBottom: 14 }}>
              <AlertCircle size={ICON.md} />Couldn't load Shopify data: {loadError}
            </div>
          )}

          <div style={S.chartRow}>
            <ChartCard
              title="Fulfilled vs Unfulfilled"
              subtitle={`${(totals.fulfilled + totals.unfulfilled).toLocaleString('en-IN')} orders in range`}
              height={240}
            >
              <FulfillmentPie fulfilled={totals.fulfilled} unfulfilled={totals.unfulfilled} />
            </ChartCard>
          </div>

          <ChartCard
            title="Fulfillment over time"
            subtitle={GRANULARITY_TABS.find(t => t.key === granularity)?.label + ' buckets'}
            height={260}
          >
            <FulfillmentBars data={chartData} />
          </ChartCard>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', margin: '10px 0' }}>
            <span style={{ fontSize: 12.5, color: 'var(--shopify-text)' }}>
              {loading ? 'Loading…' : `${orderRows.length} unfulfilled order${orderRows.length === 1 ? '' : 's'} in this range`}
            </span>
            {!loading && <Pagination table={table} />}
          </div>

          <div style={S.tableWrap}>
            <div className="shopify-table-header" style={S.tableHeader}>
              {table.getHeaderGroups().map(hg => hg.headers.map(header => (
                <div key={header.id} style={{ ...S.th, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }} onClick={header.column.getToggleSortingHandler()}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {{ asc: <ArrowUp size={11} />, desc: <ArrowDown size={11} /> }[header.column.getIsSorted()] ?? (header.column.getCanSort() ? <ArrowUpDown size={11} style={{ opacity: 0.4 }} /> : null)}
                </div>
              )))}
            </div>

            {loading && (
              <div style={S.skeletonWrap}>
                {[...Array(6)].map((_, i) => <div key={i} className="shopify-skeleton-row" style={{ ...S.skeletonRow, animationDelay: `${i * 80}ms` }} />)}
              </div>
            )}

            {!loading && table.getRowModel().rows.map(row => {
              const o = row.original;
              const isSelected = String(o.order_id) === String(selectedOrderId);
              return (
                <div
                  key={row.id}
                  className="shopify-table-row"
                  style={{ ...S.row, background: isSelected ? 'var(--shopify-purple-tint)' : undefined }}
                  onClick={() => setSelectedOrderId(o.order_id)}
                >
                  {row.getVisibleCells().map(cell => (
                    <div key={cell.id} style={S.td}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
                  ))}
                </div>
              );
            })}
            {!loading && orderRows.length === 0 && (
              <div style={S.emptyState}>
                <AlertCircle size={ICON.lg} style={{ color: 'var(--shopify-border-soft)' }} />
                <div style={{ fontWeight: 600, color: 'var(--shopify-text-muted)' }}>No unfulfilled orders in this range</div>
                <div style={{ fontSize: 12.5, color: 'var(--shopify-text-faint)' }}>Either everything shipped, or try a wider date range.</div>
              </div>
            )}
          </div>

          {orderRows.length > 0 && (
            <div style={S.pager}>
              <span style={{ fontSize: 12.5, color: 'var(--shopify-text-muted)' }}>
                Rows {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}
                –{Math.min((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize, orderRows.length)} of {orderRows.length}
              </span>
              <Pagination table={table} />
            </div>
          )}
        </div>

        {/* Right column — always present. KPI view by default; swaps to the
            selected order's detail in place. Never an overlay. */}
        <div className="shopify-right-col" style={S.rightCol}>
          <AnimatePresence mode="wait">
            {selectedOrder ? (
              <DetailView key="detail" order={selectedOrder} onBack={() => setSelectedOrderId(null)} />
            ) : (
              <KpiView key="kpi" fulfilled={totals.fulfilled} unfulfilled={totals.unfulfilled} />
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Embedded CSS — theme tokens, calendar theming, fluid-tab pill, skeleton  */
/* rows, and a reduced-motion fallback for every JS-driven animation.      */
/* Mounted inside this component's own JSX so it unmounts with the route.  */
/* ---------------------------------------------------------------------- */
const EMBEDDED_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap');

/* Light/Dark theme tokens for this page only. --shopify-text is primary text
   ink (light-on-dark in dark mode); --shopify-accent-surface is the
   deliberately-dark chip/banner surface (Total Orders banner, table header,
   active tab pill) that must stay a distinct dark chip in BOTH themes rather
   than flip to light — the two roles share the same #231C30 hex in light mode
   but are not the same concept, so they get separate tokens. The header bar
   (#0B0A12) is permanently dark and untouched by theming.

   Scoped to :root (not just .shopify-page): Base UI's Menu.Portal renders
   popups (the Month/Year dropdowns, including the calendar's own caption
   dropdowns) directly onto document.body, OUTSIDE this component's DOM
   subtree — a var() only resolves through actual DOM ancestry, not React-tree
   nesting, so a .shopify-page-scoped variable is invisible to that portaled
   content and every color silently falls back to CSS's initial value
   (transparent). Defining these on :root means every node in the document —
   portaled or not — resolves them the same way. The --shopify-* naming means
   nothing outside this page references them, so this doesn't visually affect
   any other route. */
:root {
  --shopify-surface: #fff;            --shopify-surface-subtle: #FBFAFD;
  --shopify-border: #ECE7F2;          --shopify-border-soft: #E4DEEE;
  --shopify-text: #231C30;            --shopify-text-muted: #8C82A0;      --shopify-text-faint: #9A90AE;
  --shopify-accent-surface: #231C30;
  --shopify-purple: #6E58A6;          --shopify-purple-tint: #EEE9F8;
  --shopify-amber-tint: #F5EDDC; --shopify-amber: #9A7B3E; --shopify-amber-border: #EADFC5;
  --shopify-green-tint: #E7F0E8; --shopify-green: #5C7E60; --shopify-green-border: #D6E6D8;
  --shopify-blue-tint:  #DCEAF5; --shopify-blue:  #3A6B8A;
  --shopify-pink-tint:  #F7E5EA; --shopify-error: #C25E77;
  --shopify-page-bg: transparent;
  --shopify-shadow-sm: 0 1px 2px rgba(35,28,48,0.05);
  --shopify-shadow-md: 0 6px 20px rgba(35,28,48,0.10);
  --shopify-shadow-lg: 0 20px 50px rgba(35,28,48,0.22);
}
:root[data-shopify-theme="dark"] {
  --shopify-surface: #1A1823;          --shopify-surface-subtle: #1F1D2A;
  --shopify-border: #2D2A37;           --shopify-border-soft: #34303F;
  --shopify-text: #F0EFF5;             --shopify-text-muted: #948CA6;      --shopify-text-faint: #7C7589;
  --shopify-accent-surface: #2D2A44;
  --shopify-purple: #A48FD1;           --shopify-purple-tint: #2D2A44;
  --shopify-amber-tint: #3A331F; --shopify-amber: #D8B570; --shopify-amber-border: #4A4128;
  --shopify-green-tint: #1F2E22; --shopify-green: #8FBE97; --shopify-green-border: #2C4331;
  --shopify-blue-tint:  #1C2C38; --shopify-blue:  #7FB3D9;
  --shopify-pink-tint:  #3A2129; --shopify-error: #E08BA0;
  --shopify-page-bg: #141220;
  --shopify-shadow-sm: 0 1px 2px rgba(0,0,0,0.30);
  --shopify-shadow-md: 0 6px 20px rgba(0,0,0,0.40);
  --shopify-shadow-lg: 0 20px 50px rgba(0,0,0,0.55);
}

/* Thin, translucent scrollbars everywhere on this page — table body, right
   column, and the Month/Year/calendar popups (which render into a portal
   outside this component's own DOM subtree, hence unscoped selectors here
   rather than under a wrapper class) — with no up/down arrow buttons. This
   whole <style> tag unmounts with the page, so it can't leak into the rest of
   the portal once this route is left. */
* { scrollbar-width: thin; scrollbar-color: rgba(110,88,166,0.35) transparent; }
*::-webkit-scrollbar { width: 6px; height: 6px; }
*::-webkit-scrollbar-track { background: transparent; }
*::-webkit-scrollbar-thumb { background: rgba(110,88,166,0.35); border-radius: 999px; }
*::-webkit-scrollbar-thumb:hover { background: rgba(110,88,166,0.55); }
*::-webkit-scrollbar-button { display: none; width: 0; height: 0; }

.shopify-table-header > div:not(:last-child), .shopify-table-row > div:not(:last-child) { border-right: 1px solid var(--shopify-border); }
.shopify-table-row:nth-child(even) { background: var(--shopify-surface-subtle); }
.shopify-table-row:hover { background: var(--shopify-purple-tint); cursor: pointer; }

.shopify-tab { font-family: 'Space Grotesk'; transition: transform 0.15s ease, background 0.2s ease, color 0.2s ease, border-color 0.2s ease; cursor: pointer; }
.shopify-tab:active { transform: scale(0.96); }

.shopify-calendar .rdp-root {
  --rdp-accent-color: var(--shopify-purple);
  --rdp-accent-background-color: var(--shopify-purple-tint);
  --rdp-today-color: var(--shopify-purple);
  /* Halved from react-day-picker's defaults (44/42/36/44px) to match the rest
     of the portal's calendar sizing. */
  --rdp-day-height: 22px;
  --rdp-day-width: 22px;
  --rdp-day_button-height: 21px;
  --rdp-day_button-width: 21px;
  --rdp-nav_button-height: 1.125rem;
  --rdp-nav_button-width: 1.125rem;
  --rdp-nav-height: 1.375rem;
  font-family: 'Space Grotesk';
  font-size: 12px;
  color: var(--shopify-text);
}
.shopify-calendar .rdp-day_button:hover { background: var(--shopify-purple-tint); }
.shopify-calendar .rdp-weekday { color: var(--shopify-text-muted); }
.shopify-calendar .rdp-day_button { color: var(--shopify-text); }
.shopify-calendar .rdp-outside .rdp-day_button,
.shopify-calendar .rdp-day_button:disabled { color: var(--shopify-text-faint); }
.shopify-calendar .rdp-selected .rdp-day_button { color: #fff; }
/* The day/weekday <td> cells are auto-sized by the browser's table layout
   algorithm, which can end up wider than the --rdp-day-width hint —
   table-layout:fixed plus explicit widths on every cell forces the real
   halved size instead of just the button inside it. */
.shopify-calendar .rdp-month_grid { table-layout: fixed; }
.shopify-calendar .rdp-day, .shopify-calendar .rdp-weekday { width: 26px; max-width: 26px; padding: 0; }
/* react-day-picker's default stacks multiple months vertically; put them side
   by side instead (this page has no Tailwind to do it with a breakpoint). */
.shopify-calendar .rdp-months { display: flex; flex-direction: row; flex-wrap: nowrap; gap: 20px; }

.shopify-beam-bg { position: absolute; inset: 0; display: flex; overflow: hidden; border-radius: 16px; z-index: 0; pointer-events: none; background: #0B0A12; }
.shopify-beam-lane { position: relative; flex: 1 0 auto; width: 34px; height: 100%; }
.shopify-beam-lane::before { content: ''; position: absolute; inset: 0; left: 50%; width: 1px; background: rgba(230,222,250,0.06); transform: rotate(10deg); }
.shopify-beam {
  position: absolute; left: 50%; top: -20%; width: 2px; height: var(--length);
  transform: translateX(-50%) rotate(10deg); opacity: 0;
  background: linear-gradient(180deg, transparent, #E4DBF5, transparent);
  animation: shopify-meteor var(--duration) var(--delay) ease-in-out infinite backwards;
}
@keyframes shopify-meteor {
  0%   { top: -20%; opacity: 0; }
  12%  { opacity: var(--opacity); }
  88%  { opacity: var(--opacity); }
  100% { top: 120%; opacity: 0; }
}
@media (prefers-reduced-motion: reduce) { .shopify-beam { animation: none; opacity: 0; } }

.shopify-blob-bg { position: fixed; inset: 0; z-index: 0; overflow: hidden; pointer-events: none; }
.shopify-blob {
  position: absolute; width: 480px; height: 480px; border-radius: 50%;
  filter: blur(90px); opacity: 0.55; mix-blend-mode: screen;
  animation: shopify-pop-blob 5s ease-in-out infinite;
}
.shopify-blob:nth-child(1) { top: -10%; left: -8%; }
.shopify-blob:nth-child(2) { top: -14%; right: -10%; }
.shopify-blob:nth-child(3) { bottom: -16%; left: 30%; }
@keyframes shopify-pop-blob {
  0%   { transform: scale(1); }
  33%  { transform: scale(1.2); }
  66%  { transform: scale(0.8); }
  100% { transform: scale(1); }
}
@media (prefers-reduced-motion: reduce) { .shopify-blob { animation: none; } }

@keyframes shopify-skeleton-shimmer {
  0%   { opacity: 0.55; }
  50%  { opacity: 1; }
  100% { opacity: 0.55; }
}
.shopify-skeleton-row { animation: shopify-skeleton-shimmer 1.3s ease-in-out infinite; }

@media (prefers-reduced-motion: reduce) {
  .shopify-tab { transition: none; }
  .shopify-skeleton-row { animation: none; }
}

/* Responsive: below this, the 2/3+1/3 split just squeezed the right column
   unreadably narrow instead of adapting — stack it under the table instead.
   !important is required because inline styles otherwise always beat a plain
   media-query rule regardless of specificity. */
@media (max-width: 1024px) {
  .shopify-body { flex-direction: column !important; }
  .shopify-right-col {
    position: static !important; top: auto !important; max-height: none !important;
    width: 100% !important; max-width: none !important; min-width: 0 !important;
  }
}
`;

/* ---------------------------------------------------------------------- */
/* Styles                                                                  */
/* ---------------------------------------------------------------------- */
const S = {
  page: { color: 'var(--shopify-text)', fontFamily: "'Space Grotesk', sans-serif", background: 'var(--shopify-page-bg)', position: 'relative' },
  numTab: { fontVariantNumeric: 'tabular-nums' },

  headerBar: {
    position: 'sticky', top: 0, zIndex: 20, display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
    background: '#0B0A12', borderRadius: 16, border: '1px solid rgba(255,255,255,0.08)', boxShadow: SHADOW_MD,
    padding: '14px 20px', marginBottom: 18, overflow: 'hidden',
  },
  h1: { fontFamily: "'Space Grotesk'", fontWeight: 300, fontSize: 22, margin: 0, color: '#F3EFFA', letterSpacing: '-0.01em', whiteSpace: 'nowrap' },
  headerActions: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 14, flexWrap: 'wrap', flex: 1, minWidth: 0, minHeight: 38 },
  syncNote: { fontSize: 10.5, fontWeight: 600, color: '#8A80A0', letterSpacing: '0.02em', whiteSpace: 'nowrap' },

  btnGhost: { display: 'flex', alignItems: 'center', gap: 7, padding: '10px 16px', borderRadius: 10, border: '1px solid var(--shopify-border-soft)', background: 'var(--shopify-surface)', color: 'var(--shopify-text)', fontWeight: 500, fontSize: 13, cursor: 'pointer', fontFamily: "'Space Grotesk'", whiteSpace: 'nowrap' },
  btnGreen: { display: 'flex', alignItems: 'center', gap: 7, padding: '10px 16px', borderRadius: 10, border: 'none', background: '#5C7E60', color: '#fff', fontWeight: 500, fontSize: 13, cursor: 'pointer', fontFamily: "'Space Grotesk'", whiteSpace: 'nowrap' },

  body: { display: 'flex', gap: 20, alignItems: 'flex-start', position: 'relative', zIndex: 1 },
  leftCol: { flex: '1 1 66%', minWidth: 0 },
  rightCol: {
    flex: '0 0 33%', minWidth: 280, maxWidth: 380,
    position: 'sticky', top: STICKY_TOP, alignSelf: 'flex-start',
    maxHeight: `calc(100vh - ${STICKY_TOP + 24}px)`, overflowY: 'auto',
    background: 'var(--shopify-surface)', borderRadius: 16, border: '1px solid var(--shopify-border)', boxShadow: SHADOW_SM,
  },

  filterBar: {
    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
    background: 'var(--shopify-surface)', border: '1px solid var(--shopify-border)', borderRadius: 14, boxShadow: SHADOW_SM,
    padding: '10px 14px', marginBottom: 14,
  },
  filterDivider: { width: 1, alignSelf: 'stretch', background: 'var(--shopify-border)', margin: '0 2px' },

  fluidNav: { display: 'inline-flex' },
  fluidList: { position: 'relative', display: 'flex', gap: 2, background: 'var(--shopify-surface-subtle)', borderRadius: 999, padding: 4 },
  fluidTab: { position: 'relative', border: 'none', background: 'transparent', padding: '8px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 600 },
  fluidPill: { position: 'absolute', inset: 0, borderRadius: 999, background: 'var(--shopify-accent-surface)', zIndex: 0 },
  fluidTabContent: { position: 'relative', zIndex: 1, display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' },

  dateBtn: { display: 'flex', alignItems: 'center', gap: 7, padding: '8px 13px', borderRadius: 9, border: '1px solid var(--shopify-border)', background: 'var(--shopify-surface)', color: 'var(--shopify-text)', fontSize: 12.5 },
  clearFiltersBtn: { display: 'flex', alignItems: 'center', gap: 5, padding: '8px 12px', borderRadius: 9, border: '1px solid var(--shopify-border-soft)', background: 'var(--shopify-purple-tint)', color: 'var(--shopify-purple)', fontSize: 12.5, fontWeight: 600 },
  calendarCaptionTrigger: { display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', background: 'transparent', color: 'var(--shopify-text)', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: "'Space Grotesk'", padding: '2px 4px' },
  // Well above everything else in the app — a popup like this kept appearing
  // behind other elements at z-index 150 despite that seeming high enough in
  // isolation, because the actual stacking-context comparison happens at
  // whatever ancestor establishes one, not against this page's own elements.
  calendarPopover: { position: 'absolute', top: '110%', left: 0, zIndex: 500, background: 'var(--shopify-surface)', borderRadius: 14, boxShadow: SHADOW_LG, border: '1px solid var(--shopify-border)', padding: 10 },
  clearCalBtn: { width: '100%', marginTop: 6, padding: '8px', borderRadius: 8, border: '1px solid var(--shopify-border-soft)', background: 'var(--shopify-purple-tint)', color: 'var(--shopify-text-muted)', fontSize: 12.5, cursor: 'pointer', fontFamily: "'Space Grotesk'" },

  menuPopup: { background: 'var(--shopify-surface)', borderRadius: 12, boxShadow: SHADOW_LG, border: '1px solid var(--shopify-border)', padding: 6, minWidth: 140, maxHeight: 280, overflowY: 'auto', zIndex: 500 },
  menuItem: { padding: '9px 12px', borderRadius: 8, fontSize: 13, color: 'var(--shopify-text)', cursor: 'pointer', fontFamily: "'Space Grotesk'", outline: 'none' },

  chartRow: { display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 14 },
  chartCard: {
    flex: '1 1 320px', minWidth: 0, background: 'var(--shopify-surface)', border: '1px solid var(--shopify-border)',
    borderRadius: 16, boxShadow: SHADOW_SM, padding: '14px 16px', marginBottom: 14,
  },
  chartTitle: { fontSize: 13.5, fontWeight: 600, color: 'var(--shopify-text)' },
  chartSubtitle: { fontSize: 11.5, color: 'var(--shopify-text-muted)', marginTop: 2 },
  chartEmpty: { height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12.5, color: 'var(--shopify-text-faint)' },

  // `overflowX: 'auto'` alone forces the browser to compute overflow-y as a
  // scroll container too (CSS spec: mixing 'visible' with a scrolling value on
  // the other axis promotes 'visible' to 'auto') — so this box was always going
  // to be its OWN sticky containing block, never the page. Leaning into that
  // instead of fighting it: a fixed max-height + explicit overflowY make it a
  // genuine self-scrolling table with its own sticky header (top: 0, not
  // page-relative) — same pattern as the right column.
  tableWrap: {
    borderRadius: 16, background: 'var(--shopify-surface)', border: '1px solid var(--shopify-border)', boxShadow: SHADOW_SM,
    overflowX: 'auto', overflowY: 'auto', maxHeight: `calc(100vh - ${STICKY_TOP + 24}px)`,
  },
  pager: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 4px' },
  paginationNav: { display: 'flex', alignItems: 'center', gap: 4 },
  paginationArrow: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 8, border: '1px solid var(--shopify-border-soft)', background: 'var(--shopify-surface)', color: 'var(--shopify-text)', cursor: 'pointer' },
  paginationLink: { minWidth: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, border: '1px solid transparent', background: 'transparent', color: 'var(--shopify-text-muted)', fontSize: 12.5, fontWeight: 500, cursor: 'pointer', fontFamily: "'Space Grotesk'" },
  paginationLinkActive: { background: '#6E58A6', color: '#fff', fontWeight: 700 },
  paginationEllipsis: { minWidth: 24, textAlign: 'center', color: 'var(--shopify-text-faint)', fontSize: 12.5 },
  tableHeader: {
    display: 'grid', gridTemplateColumns: GRID_COLS,
    background: 'var(--shopify-accent-surface)', color: '#fff', minWidth: TABLE_MIN_WIDTH, position: 'sticky', top: 0, zIndex: 5,
    borderRadius: '16px 16px 0 0',
  },
  th: { padding: '13px 16px', fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textAlign: 'left' },
  row: { display: 'grid', gridTemplateColumns: GRID_COLS, borderBottom: '1px solid var(--shopify-border)', alignItems: 'center', minWidth: TABLE_MIN_WIDTH },
  td: { padding: '13px 16px', fontSize: 13, textAlign: 'left', display: 'flex', alignItems: 'center' },
  emptyState: { padding: '48px 18px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 },
  skeletonWrap: { padding: 14, display: 'flex', flexDirection: 'column', gap: 10 },
  skeletonRow: { height: 44, borderRadius: 10, background: 'linear-gradient(90deg, var(--shopify-border), var(--shopify-surface-subtle), var(--shopify-border))' },

  statusBadge: { padding: '5px 12px', borderRadius: 9, fontSize: 12, fontWeight: 600, border: '1px solid', textTransform: 'capitalize' },
  statLabel: { fontSize: 10.5, color: 'var(--shopify-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' },

  bentoGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, padding: 16 },
  bentoSpan2: { gridColumn: 'span 2' },
  bentoCard: { borderRadius: 16, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 6, minHeight: 76, justifyContent: 'center' },

  backBtn: { display: 'flex', alignItems: 'center', gap: 6, border: 'none', background: 'transparent', color: 'var(--shopify-purple)', fontWeight: 600, fontSize: 12.5, cursor: 'pointer', fontFamily: "'Space Grotesk'", padding: '16px 20px 0' },
  panelHeader: { padding: '10px 20px 14px' },
};
